import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft, FiArrowRight, FiCheck, FiCheckCircle, FiClock, FiFile,
  FiCreditCard, FiDollarSign, FiInfo, FiLock, FiPaperclip, FiShield,
  FiTrash2, FiUploadCloud, FiX,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { api } from '../services/api';
import {
  getServiceOrderPriceSummary,
  getServicePaymentOptions,
} from '../utils/serviceOrderPricing';
import './ServiceOrderModal.css';

const ServiceOrderModal = ({ service, group, plan, onClose }) => {
  const { t } = useTranslation();
  const { locale, localePath, formatDate, formatNumber } = useLocale();
  const contactMethods = useMemo(() => [
    ['phone', t('serviceUi.order.contactMethods.phone')],
    ['whatsapp', t('serviceUi.order.contactMethods.whatsapp')],
    ['email', t('serviceUi.order.contactMethods.email')],
  ], [t]);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isWebsiteSupport = service.slug === 'website-support';
  const isQuoteRequest = group.mode === 'request';
  const isRecurringSupport = isWebsiteSupport && group.id === 'monthly-support' && !isQuoteRequest;
  const paymentEnabled = Boolean(service.paymentEnabled && service.paymentProvider !== 'none');
  const marketCurrency = service.currencyLabel || locale.currencyLabel || plan?.currency || '';
  const request = group.request || {};
  const selectionTitle = isQuoteRequest
    ? request.title || group.title
    : plan?.name || group.title;
  const fields = useMemo(() => (service.orderForm?.fields || []).filter((item) => (
    item.isActive !== false
    && !(
      isWebsiteSupport
      && (
        (!isRecurringSupport && item.id === 'support_type')
        || (isRecurringSupport && item.id === 'request_kind')
      )
    )
  )), [isRecurringSupport, isWebsiteSupport, service.orderForm]);
  const configuredMaxAttachments = Number(service.orderForm?.maxAttachments);
  const maxAttachments = Number.isFinite(configuredMaxAttachments) ? Math.max(0, Math.min(5, configuredMaxAttachments)) : 5;
  const maxAttachmentSizeMb = Math.max(1, Math.min(25, Number(service.orderForm?.maxAttachmentSizeMb) || 10));
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    projectTitle: '', description: '', targetDate: '', budgetRange: '', referenceLinks: '',
    phone: user?.phone || '', preferredMethod: 'phone', termsAccepted: false,
  });
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [paymentOption, setPaymentOption] = useState(isRecurringSupport && paymentEnabled ? 'full' : 'review_later');
  const priceSummary = useMemo(() => getServiceOrderPriceSummary({
    serviceSlug: service.slug,
    groupId: group.id,
    durationOptions: group.durationOptions,
    plan: plan || {},
    answers,
  }), [answers, group.durationOptions, group.id, plan, service.slug]);
  const supportDurationChoices = useMemo(() => {
    if (!isRecurringSupport) return [];
    const durationField = fields.find((field) => field.id === 'support_type');
    return (durationField?.options || []).map((label) => ({
      label,
      summary: getServiceOrderPriceSummary({
        serviceSlug: service.slug,
        groupId: group.id,
        durationOptions: group.durationOptions,
        plan,
        answers: { ...answers, support_type: label },
      }),
    }));
  }, [answers, fields, group.durationOptions, group.id, isRecurringSupport, plan, service.slug]);
  const paymentOptions = useMemo(
    () => getServicePaymentOptions(priceSummary, { paymentEnabled }),
    [paymentEnabled, priceSummary]
  );
  const selectedPaymentOption = paymentOptions.find((option) => option.id === paymentOption)
    || paymentOptions[0];
  const displayPrice = priceSummary.displayAmount || t('serviceUi.order.review.priceAfterReview');
  const effectivePaymentOption = selectedPaymentOption?.id || 'review_later';
  const createdPaymentMode = createdOrder?.paymentSchedule?.mode || effectivePaymentOption;
  const createdPayableStage = createdOrder?.paymentSchedule?.installments?.find(
    (stage) => ['pending', 'failed'].includes(stage.status)
  );
  const summaryDetails = useMemo(() => {
    const details = [
      [t('serviceUi.order.summary.projectTitle'), form.projectTitle.trim() || t('serviceUi.order.summary.missing')],
    ];
    if (isWebsiteSupport) {
      details.push([t('serviceUi.order.summary.website'), String(answers.website_url || '').trim() || t('serviceUi.order.summary.missing')]);
      if (isRecurringSupport) {
        details.push([t('serviceUi.order.summary.supportDuration'), String(answers.support_type || '').trim() || t('serviceUi.order.summary.notSelected')]);
      }
      if (isQuoteRequest) {
        details.push([t('serviceUi.order.summary.requestType'), String(answers.request_kind || '').trim() || t('serviceUi.order.summary.notSelected')]);
      }
      details.push(
        [t('serviceUi.order.summary.websiteType'), String(answers.website_type || '').trim() || t('serviceUi.order.summary.notSelected')],
        [t('serviceUi.order.summary.platform'), String(answers.platform || '').trim() || t('serviceUi.order.summary.notSelected')]
      );
    }
    if (form.targetDate) {
      details.push([t('serviceUi.order.summary.targetDate'), formatDate(`${form.targetDate}T00:00:00`)]);
    }
    if (form.budgetRange.trim()) details.push([t('serviceUi.order.summary.budget'), form.budgetRange.trim()]);
    return details;
  }, [answers, form.budgetRange, form.projectTitle, form.targetDate, formatDate, isQuoteRequest, isRecurringSupport, isWebsiteSupport, t]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const keydown = (event) => { if (event.key === 'Escape' && !submitting) onClose(); };
    window.addEventListener('keydown', keydown);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', keydown);
    };
  }, [onClose, submitting]);

  useEffect(() => {
    if (user?.phone && !form.phone) setForm((current) => ({ ...current, phone: user.phone }));
  }, [form.phone, user]);

  useEffect(() => {
    if (!paymentOptions.some((option) => option.id === paymentOption)) {
      setPaymentOption(paymentOptions[0]?.id || 'review_later');
    }
  }, [paymentOption, paymentOptions]);

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError('');
  };
  const updateAnswer = (field, value) => {
    setAnswers((current) => ({ ...current, [field.id]: value }));
    setError('');
  };

  const validateStep = () => {
    if (step === 1) {
      if (!form.projectTitle.trim() || !form.description.trim()) {
        return t(isWebsiteSupport ? 'serviceUi.order.errors.supportRequired' : 'serviceUi.order.errors.projectRequired');
      }
      const missing = fields.find((field) => field.required && !(Array.isArray(answers[field.id]) ? answers[field.id].length : String(answers[field.id] ?? '').trim()));
      if (missing) return t('serviceUi.order.errors.fieldRequired', { label: missing.label });
    }
    if (step === 2) {
      if (!form.phone.trim()) return t('serviceUi.order.errors.phoneRequired');
      if (!form.termsAccepted) return t('serviceUi.order.errors.termsRequired');
    }
    return '';
  };

  const nextStep = () => {
    const message = validateStep();
    if (message) return setError(message);
    setStep((current) => Math.min(3, current + 1));
  };

  const handleFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    const maxBytes = maxAttachmentSizeMb * 1024 * 1024;
    if (files.length + selected.length > maxAttachments) return setError(t('serviceUi.order.errors.maxFiles', { count: formatNumber(maxAttachments) }));
    if (selected.some((file) => file.size > maxBytes)) return setError(t('serviceUi.order.errors.maxFileSize', { size: formatNumber(maxAttachmentSizeMb) }));
    setFiles((current) => [...current, ...selected]);
    setError('');
  };

  const goToLogin = () => {
    sessionStorage.setItem('pendingServicePlan', JSON.stringify({
      serviceId: service._id || service.id,
      groupId: group.id,
      planId: plan?.id || '',
      selectionType: isQuoteRequest ? 'quote_request' : 'plan',
    }));
    navigate(localePath('/login'), { state: { from: `${location.pathname}${location.search}${location.hash}` } });
  };

  const submit = async () => {
    const message = validateStep();
    if (message) return setError(message);
    setSubmitting(true);
    setError('');
    try {
      const body = new FormData();
      body.append('serviceId', service._id || service.id);
      body.append('groupId', group.id);
      body.append('planId', plan?.id || '');
      body.append('locale', service.locale || locale.code);
      Object.entries(form).forEach(([key, value]) => body.append(key, value == null ? '' : String(value)));
      body.append('paymentOption', isRecurringSupport && paymentEnabled ? effectivePaymentOption : 'review_later');
      body.append('answers', JSON.stringify(fields.map((field) => ({ fieldId: field.id, value: answers[field.id] ?? '' }))));
      body.append('referenceLinks', JSON.stringify(form.referenceLinks.split(/\r?\n/).map((item) => item.trim()).filter(Boolean)));
      files.forEach((file) => body.append('attachments', file));
      const response = await api.upload('/api/service-orders', body);
      setCreatedOrder(response.data);
      sessionStorage.removeItem('pendingServicePlan');
      if (paymentEnabled && isRecurringSupport && effectivePaymentOption !== 'review_later') {
        try {
          const paymentResponse = await api.post(
            `/api/service-orders/${response.data._id}/payment-request`,
            {}
          );
          window.location.assign(paymentResponse.data.paymentUrl);
          return;
        } catch (paymentError) {
          setError(
            paymentError.data?.message
            || paymentError.message
            || t('serviceUi.order.errors.paymentStart')
          );
        }
      }
    } catch (requestError) {
      setError(requestError.data?.message || requestError.message || t('serviceUi.order.errors.submit'));
    } finally {
      setSubmitting(false);
    }
  };

  const startCreatedOrderPayment = async () => {
    if (!createdOrder) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await api.post(
        `/api/service-orders/${createdOrder._id}/payment-request`,
        {}
      );
      window.location.assign(response.data.paymentUrl);
    } catch (paymentError) {
      setError(paymentError.data?.message || paymentError.message || t('serviceUi.order.errors.gateway'));
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = answers[field.id] ?? (field.type === 'checkbox' ? [] : '');
    const isWebsiteAddress = isWebsiteSupport && field.id === 'website_url';
    if (isRecurringSupport && field.id === 'support_type') {
      return (
        <div className="service-order-duration-grid">
          {supportDurationChoices.map(({ label, summary }) => (
            <label
              key={label}
              className={`${value === label ? 'is-selected' : ''} ${summary.durationPopular ? 'is-popular' : ''}`}
            >
              <input
                type="radio"
                name={`order-${field.id}`}
                checked={value === label}
                onChange={() => updateAnswer(field, label)}
              />
              {summary.durationBadge && <span className="service-order-duration-badge">{summary.durationBadge}</span>}
              <span className="service-order-duration-title">
                <strong>{formatNumber(summary.durationMonths)} {t('serviceUi.common.month')}</strong>
                <small>{t(summary.durationMonths === 1 ? 'serviceUi.order.duration.flexible' : 'serviceUi.order.duration.longTerm')}</small>
              </span>
              <span className="service-order-duration-price">
                <strong>{summary.displayAmount} <small>{summary.currency}</small></strong>
                {summary.discountPercent > 0 && (
                  <del>{formatNumber(summary.regularTotalAmount, { maximumFractionDigits: 2 })} {summary.currency}</del>
                )}
              </span>
              <span className="service-order-duration-monthly">
                {t('serviceUi.order.duration.monthly', { amount: formatNumber(summary.effectiveMonthlyAmount, { maximumFractionDigits: 2 }), currency: summary.currency })}
              </span>
              {summary.savingsMarketAmount > 0
                ? <em>−{formatNumber(summary.discountPercent)}٪ · {formatNumber(summary.savingsMarketAmount)} {marketCurrency}</em>
                : <em className="is-neutral">{t('serviceUi.order.duration.noDiscount')}</em>}
            </label>
          ))}
        </div>
      );
    }
    if (field.type === 'textarea') return <textarea rows="4" value={value} placeholder={field.placeholder} onChange={(event) => updateAnswer(field, event.target.value)} />;
    if (field.type === 'select') return <select value={value} onChange={(event) => updateAnswer(field, event.target.value)}><option value="">{t('serviceUi.common.select')}</option>{field.options.map((option) => <option key={option} value={option}>{option}</option>)}</select>;
    if (field.type === 'radio') return <div className="service-order-choices">{field.options.map((option) => <label key={option}><input type="radio" name={`order-${field.id}`} checked={value === option} onChange={() => updateAnswer(field, option)} /><span>{option}</span></label>)}</div>;
    if (field.type === 'checkbox') return <div className="service-order-choices">{field.options.map((option) => <label key={option}><input type="checkbox" checked={value.includes(option)} onChange={(event) => updateAnswer(field, event.target.checked ? [...value, option] : value.filter((item) => item !== option))} /><span>{option}</span></label>)}</div>;
    return <input type={field.type === 'url' && !isWebsiteAddress ? 'url' : field.type === 'number' ? 'number' : 'text'} inputMode={field.type === 'url' ? 'url' : undefined} dir={field.type === 'url' ? 'ltr' : undefined} autoCapitalize={field.type === 'url' ? 'none' : undefined} spellCheck={field.type === 'url' ? false : undefined} value={value} placeholder={isWebsiteAddress ? 'haryad.com' : field.placeholder} onChange={(event) => updateAnswer(field, event.target.value)} />;
  };

  return createPortal(
    <div className="service-order-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget && !submitting) onClose(); }}>
      <section className="service-order-modal" role="dialog" aria-modal="true" aria-labelledby="service-order-title">
        <button type="button" className="service-order-close ui-close-button" onClick={onClose} disabled={submitting} aria-label={t('serviceUi.common.close')}><FiX /></button>
        <header className="service-order-header">
          <div>
            <span>{t(isQuoteRequest ? 'serviceUi.order.header.quote' : 'serviceUi.order.header.order')}</span>
            <h2 id="service-order-title">{service.title} · {selectionTitle}</h2>
            <p>
              {isQuoteRequest
                ? request.badge || t('serviceUi.order.header.quoteHint')
                : `${displayPrice} ${priceSummary.currency} · ${priceSummary.periodLabel}`}
            </p>
          </div>
          {!createdOrder && <div className="service-order-response"><FiClock /><span>{t('serviceUi.order.header.response')}<strong>{formatNumber(service.orderForm?.expectedResponseHours || 24)} {t('serviceUi.common.hour')}</strong></span></div>}
        </header>

        {!isAuthenticated ? (
          <div className="service-order-auth">
            <span><FiLock /></span><h3>{t('serviceUi.order.auth.title')}</h3>
            <p>{t('serviceUi.order.auth.description')}</p>
            <button type="button" onClick={goToLogin}>{t('serviceUi.order.auth.action')} <FiArrowLeft /></button>
          </div>
        ) : createdOrder ? (
          <div className="service-order-success">
            <span><FiCheckCircle /></span><h3>{t('serviceUi.order.success.title')}</h3>
            <p>
              {t('serviceUi.order.success.tracking', { code: createdOrder.orderNumber })}
              {createdPaymentMode === 'review_later'
                ? ` ${t('serviceUi.order.success.review')}`
                : ` ${t('serviceUi.order.success.payment')}`}
            </p>
            {error && <div className="service-order-error"><FiInfo /> {error}</div>}
            <div>
              {createdPaymentMode !== 'review_later' && createdPayableStage && (
                <button type="button" onClick={startCreatedOrderPayment} disabled={submitting}>
                  <FiCreditCard /> {submitting ? t('serviceUi.order.success.connecting') : `${formatNumber(createdPayableStage.amount)} ${createdOrder.currencyLabel || marketCurrency}`}
                </button>
              )}
              <button type="button" className={createdPaymentMode !== 'review_later' ? 'is-secondary' : ''} onClick={() => navigate(localePath(`/user-panel/service-orders/${createdOrder._id}`))}>{t('serviceUi.order.success.track')}</button>
              <button type="button" className="is-secondary" onClick={onClose}>{t('serviceUi.common.close')}</button>
            </div>
          </div>
        ) : (
          <>
            <div className="service-order-stepper">{['project', 'contact', 'review'].map((key, index) => <div key={key} className={step >= index + 1 ? 'is-active' : ''}><span>{step > index + 1 ? <FiCheck /> : formatNumber(index + 1)}</span><small>{t(`serviceUi.order.steps.${key}`)}</small></div>)}</div>
            <div className="service-order-body">
              <main>
                {step === 1 && <div className="service-order-fields">
                  <div className="service-order-note"><FiInfo /><p>{service.orderForm?.introText || t('serviceUi.order.form.intro')}</p></div>
                  <label className="service-order-field"><span>{t(isQuoteRequest ? 'serviceUi.order.form.requestTitle' : 'serviceUi.order.form.projectTitle')} <b>*</b></span><input value={form.projectTitle} onChange={(event) => update('projectTitle', event.target.value)} placeholder={t(isQuoteRequest ? 'serviceUi.order.form.requestTitlePlaceholder' : isWebsiteSupport ? 'serviceUi.order.form.supportTitlePlaceholder' : 'serviceUi.order.form.projectTitlePlaceholder')} /></label>
                  <label className="service-order-field is-full"><span>{t('serviceUi.order.form.description')} <b>*</b></span><textarea rows="5" value={form.description} onChange={(event) => update('description', event.target.value)} placeholder={t(isWebsiteSupport ? 'serviceUi.order.form.supportDescriptionPlaceholder' : 'serviceUi.order.form.descriptionPlaceholder')} /></label>
                  {fields.map((field) => <div className={`service-order-field ${['textarea', 'radio', 'checkbox'].includes(field.type) ? 'is-full' : ''}`} key={field.id}><span>{field.label} {field.required && <b>*</b>}</span>{renderField(field)}{field.helpText && <small>{field.helpText}</small>}</div>)}
                  <label className="service-order-field"><span>{t('serviceUi.order.form.targetDate')}</span><input type="date" value={form.targetDate} min={new Date().toISOString().slice(0, 10)} onChange={(event) => update('targetDate', event.target.value)} /></label>
                  <label className="service-order-field"><span>{t('serviceUi.order.form.budget')}</span><input value={form.budgetRange} onChange={(event) => update('budgetRange', event.target.value)} placeholder={t(isQuoteRequest ? 'serviceUi.order.form.quoteBudgetPlaceholder' : isWebsiteSupport ? 'serviceUi.order.form.supportBudgetPlaceholder' : 'serviceUi.order.form.budgetPlaceholder')} /></label>
                  <label className="service-order-field is-full"><span>{t(isWebsiteSupport ? 'serviceUi.order.form.supportLinks' : 'serviceUi.order.form.links')}</span><textarea rows="3" dir="auto" value={form.referenceLinks} onChange={(event) => update('referenceLinks', event.target.value)} placeholder={t(isWebsiteSupport ? 'serviceUi.order.form.supportLinksPlaceholder' : 'serviceUi.order.form.linksPlaceholder')} /></label>
                </div>}

                {step === 2 && <div className="service-order-fields">
                  <div className="service-order-account"><span>{(user.name || '?').charAt(0)}</span><div><small>{t('serviceUi.order.form.account')}</small><strong>{user.name}</strong><em>{user.email}</em></div><FiShield /></div>
                  <label className="service-order-field"><span>{t('serviceUi.order.form.phone')} <b>*</b></span><input type="tel" value={form.phone} onChange={(event) => update('phone', event.target.value)} placeholder="09123456789" /></label>
                  <div className="service-order-field"><span>{t('serviceUi.order.form.preferredContact')}</span><div className="service-order-choices">{contactMethods.map(([id, label]) => <label key={id}><input type="radio" name="contact-method" checked={form.preferredMethod === id} onChange={() => update('preferredMethod', id)} /><span>{label}</span></label>)}</div></div>
                  {maxAttachments > 0 && <div className="service-order-field is-full"><span>{t('serviceUi.order.form.projectFiles')}</span><label className="service-order-dropzone"><FiUploadCloud /><strong>{t('serviceUi.order.form.chooseFile')}</strong><small>{t('serviceUi.order.form.fileHint', { count: formatNumber(maxAttachments) })}</small><input type="file" multiple accept=".jpg,.jpeg,.png,.webp,.gif,.avif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar" onChange={handleFiles} /></label>{files.length > 0 && <div className="service-order-files">{files.map((file, index) => <div key={`${file.name}-${index}`}><FiFile /><span><strong>{file.name}</strong><small>{formatNumber(file.size / 1024 / 1024, { maximumFractionDigits: 2 })} MB</small></span><button type="button" onClick={() => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}><FiTrash2 /></button></div>)}</div>}</div>}
                  <label className="service-order-terms is-full">
                    <input type="checkbox" checked={form.termsAccepted} onChange={(event) => update('termsAccepted', event.target.checked)} />
                    <span>
                      {isQuoteRequest
                        ? t('serviceUi.order.form.quoteTerms')
                        : t('serviceUi.order.form.supportTerms')}
                    </span>
                  </label>
                </div>}

                {step === 3 && <div className="service-order-review">
                  <div>
                    <span>{t(isQuoteRequest ? 'serviceUi.order.review.serviceType' : 'serviceUi.order.review.servicePlan')}</span>
                    <strong>{service.title} · {selectionTitle}</strong>
                    <small>
                      {isQuoteRequest
                        ? request.badge || t('serviceUi.order.review.priceAfterReview')
                        : `${displayPrice} ${priceSummary.currency} · ${priceSummary.periodLabel}`}
                    </small>
                  </div>
                  <div><span>{t('serviceUi.order.review.project')}</span><strong>{form.projectTitle}</strong><p>{form.description}</p></div>
                  <div><span>{t('serviceUi.order.review.contact')}</span><strong>{form.phone}</strong><small>{contactMethods.find(([id]) => id === form.preferredMethod)?.[1]}</small></div>
                  <div><span>{t('serviceUi.order.review.attachments')}</span><strong>{formatNumber(files.length)} {t('serviceUi.common.file')}</strong><small>{files.map((file) => file.name).join(', ') || t('serviceUi.order.review.noAttachments')}</small></div>
                  {isRecurringSupport && (
                    <section className="service-order-payment-choice">
                      <header>
                        <span><FiCreditCard /></span>
                        <div>
                          <h4>{t('serviceUi.order.review.paymentMethod')}</h4>
                          <p>{t('serviceUi.order.review.paymentDescription')}</p>
                        </div>
                      </header>
                      <div>
                        {paymentOptions.map((option) => {
                          const optionTitle = option.id === 'review_later'
                            ? t('serviceUi.order.paymentOptions.reviewLater')
                            : option.id === 'full'
                              ? t('serviceUi.order.paymentOptions.full')
                              : t('serviceUi.order.paymentOptions.initial');
                          const optionDescription = option.id === 'full'
                            ? t('serviceUi.order.paymentOptions.fullHint')
                            : option.id === 'two_stage'
                              ? t(priceSummary.isRecurringSupport
                                ? 'serviceUi.order.paymentOptions.initialMid'
                                : 'serviceUi.order.paymentOptions.initialBeforeDelivery')
                              : '';
                          return (
                          <label className={paymentOption === option.id ? 'is-selected' : ''} key={option.id}>
                            <input
                              type="radio"
                              name="service-payment-option"
                              checked={paymentOption === option.id}
                              onChange={() => setPaymentOption(option.id)}
                            />
                            <span>
                              <strong>{optionTitle}</strong>
                              <small>{optionDescription}</small>
                            </span>
                            <em>
                              {option.dueNow > 0 ? `${formatNumber(option.dueNow)} ${marketCurrency}` : t('serviceUi.order.review.noPayment')}
                              {option.remaining > 0 && option.id !== 'review_later' && (
                                <small>{formatNumber(option.remaining)} {marketCurrency}</small>
                              )}
                            </em>
                          </label>
                          );
                        })}
                      </div>
                    </section>
                  )}
                  <div className="service-order-review-security"><FiShield /><p>{t(isRecurringSupport && effectivePaymentOption !== 'review_later' ? 'serviceUi.order.review.gatewaySecurity' : 'serviceUi.order.review.reviewSecurity')}</p></div>
                </div>}
                {error && <div className="service-order-error"><FiInfo /> {error}</div>}
              </main>
              <aside className="service-order-summary">
                <div className="service-order-summary-heading">
                  <span>{t(isQuoteRequest ? 'serviceUi.order.live.request' : 'serviceUi.order.live.plan')}</span>
                  <h3>{selectionTitle}</h3>
                  {isQuoteRequest ? (
                    <div className="service-order-summary-price-shell is-quote-request">
                      <strong className="service-order-summary-price">{t('serviceUi.order.live.priceAfterReview')}</strong>
                      <em>{t('serviceUi.order.live.noPaymentNow')}</em>
                    </div>
                  ) : (
                    <div className="service-order-summary-price-shell">
                      {priceSummary.isDurationAdjusted && priceSummary.discountPercent > 0 && (
                        <del className="service-order-summary-old-price">
                          {formatNumber(priceSummary.regularTotalAmount, { maximumFractionDigits: 2 })} {priceSummary.currency}
                        </del>
                      )}
                      <strong
                        key={`${plan?.id || group.id}-${priceSummary.isDurationAdjusted ? priceSummary.durationMonths : 'base'}`}
                        className="service-order-summary-price"
                      >
                        {displayPrice} <small>{priceSummary.currency}</small>
                      </strong>
                      {priceSummary.isRecurringSupport && (
                        <em>
                          {priceSummary.isDurationAdjusted
                            ? t('serviceUi.order.live.duration', { label: priceSummary.effectiveMonthlyLabel, count: formatNumber(priceSummary.durationMonths) })
                            : t('serviceUi.order.duration.chooseHint')}
                        </em>
                      )}
                      {priceSummary.savingsMarketAmount > 0 && (
                        <span className="service-order-summary-saving">
                          −{formatNumber(priceSummary.discountPercent)}٪ · {formatNumber(priceSummary.savingsMarketAmount)} {marketCurrency}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <dl className="service-order-summary-live">
                  {summaryDetails.map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd title={value}>{value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="service-order-summary-features">
                  {(isQuoteRequest
                    ? (request.highlights || []).slice(0, 5).map((text) => ({ text }))
                    : (plan?.features || []).filter((feature) => feature.included !== false).slice(0, isWebsiteSupport ? 4 : 7)
                  ).map((feature) => <p key={feature.text}><FiCheck /> {feature.text}</p>)}
                </div>
                {isRecurringSupport && selectedPaymentOption && (
                  <div className="service-order-summary-payment">
                    <FiDollarSign />
                    <span>
                      <small>{t('serviceUi.order.live.paymentNow')}</small>
                      <strong>
                        {selectedPaymentOption.dueNow > 0
                          ? `${formatNumber(selectedPaymentOption.dueNow)} ${marketCurrency}`
                          : t('serviceUi.order.live.afterReview')}
                      </strong>
                    </span>
                  </div>
                )}
                <small className="service-order-summary-privacy"><FiPaperclip /> {t('serviceUi.order.live.privacy')}</small>
              </aside>
            </div>
            <footer className="service-order-actions">
              <button type="button" className="is-secondary" onClick={step === 1 ? onClose : () => { setStep((current) => current - 1); setError(''); }}><FiArrowRight /> {t(step === 1 ? 'serviceUi.common.cancel' : 'serviceUi.common.previousStep')}</button>
              {step < 3 ? <button type="button" onClick={nextStep}>{t('serviceUi.common.continue')} <FiArrowLeft /></button> : <button type="button" onClick={submit} disabled={submitting}>{t(submitting ? 'serviceUi.order.actions.secureSubmitting' : paymentEnabled && isRecurringSupport && effectivePaymentOption !== 'review_later' ? 'serviceUi.order.actions.submitAndPay' : 'serviceUi.order.actions.submitFinal')} {paymentEnabled && isRecurringSupport && effectivePaymentOption !== 'review_later' ? <FiCreditCard /> : <FiCheck />}</button>}
            </footer>
          </>
        )}
      </section>
    </div>, document.body
  );
};

export default ServiceOrderModal;
